// insertConflictClause — VQE-Edit edit-actions for the ON-CONFLICT-only clause
// pieces (§2.4 S6b, decision 8 — ON DUPLICATE KEY UPDATE has neither):
//   - modify-insert-conflict-target — replace / synthesize the `(…)` target group
//   - modify-insert-conflict-action — switch DO NOTHING ↔ DO UPDATE SET …
//
// The action switch carries its own SET seed (decision 9): switching to
// `do-update` REQUIRES `setText` (the assignments text without the SET
// keyword — the UI composes it from the standard add-assignment form);
// switching to `do-nothing` drops `DO UPDATE SET …` plus any conflict-WHERE.
// These modules only validate inputs against an optional structure and
// delegate to `commitEdit` (validation throws BEFORE commit; the commit
// itself never rejects).
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.

import type { OnConflictAction, QueryStructure } from '../../../../sql-resolver/types';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';

export type ConflictClauseOptions = {
  /** Optional structure used for clause-presence validation. */
  readonly structure?: QueryStructure;
};

function assertOnConflictPresent(options: ConflictClauseOptions, label: string): void {
  if (options.structure && !options.structure.onConflict) {
    throw new Error(`${label}: statement has no ON CONFLICT clause`);
  }
}

export async function modifyInsertConflictTarget(
  columnsText: string,
  ctx: CommitEditCtx,
  options: ConflictClauseOptions = {},
): Promise<CommitOutcome> {
  const t = columnsText.trim();
  if (t.length === 0) {
    throw new Error('modifyInsertConflictTarget: columnsText must not be empty');
  }
  assertOnConflictPresent(options, 'modifyInsertConflictTarget');
  return commitEdit({ op: 'modify-insert-conflict-target', columnsText: t }, ctx);
}

export async function modifyInsertConflictAction(
  action: OnConflictAction,
  setText: string | undefined,
  ctx: CommitEditCtx,
  options: ConflictClauseOptions = {},
): Promise<CommitOutcome> {
  const seed = setText?.trim();
  if (action === 'do-update' && (seed === undefined || seed.length === 0)) {
    throw new Error(
      'modifyInsertConflictAction: switching to DO UPDATE requires a seed assignment (setText)',
    );
  }
  assertOnConflictPresent(options, 'modifyInsertConflictAction');
  return commitEdit(
    {
      op: 'modify-insert-conflict-action',
      action,
      ...(action === 'do-update' ? { setText: seed } : {}),
    },
    ctx,
  );
}
